<template>
  <div v-if="loading">
    <v-progress-linear :indeterminate="true"/>
  </div>
  <div
    v-else
    v-resize="resizeAndCenter"
    class="viewport-container"
  >
    <v-switch
      v-if="overlayStatusAllowed"
      :input-value="overlayStatus"
      :label="!!i18nTranslations ? $t('Overlay_Status') : ''"
      :value="overlayStatus"
      :class="[inputClass, 'hidden-sm-and-down', 'overlay-status-switch']"
      background-color="#fff"
      color="turquoise"
      height="56px"
      @change="toggleOverlayStatus"
    />
    <viewport
      ref="viewport"
      :exterior="exterior"
      :selected-overlay="selectedOverlay"
      :overlay-showing-status="overlayStatus"
      :overlays="overlays"
      @toggleOverlay="toggleOverlay"
      @loadedMainImg="loadedMainImg"
    />
    <point-of-interest v-if="isLoadedMainImg && isPoiMaskOn" :width="imgWidth" :height="imgHeight" :exterior="exterior"/>
    <filter-menu-button-mobile
      v-if="$vuetify.breakpoint.smAndDown && filteringAllowed"
      :exterior="exterior"
      dock-selector=".filter-menu-button-dock"
      @filtering="toggleOverlayStatus(true)"
    />
    <exterior-minimap
      v-if="minimap"
      fixed
    />
    <sunstudy-controls/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { cdnUrl } from '@/utils/urls'
import ActiveExteriorChecker from '@/mixins/activeExteriorChecker'
import Viewport from '@/components/Viewport'
import FilterMenuButtonMobile from '@/components/FilterMenuButtonMobile'
import ExteriorMinimap from '@/components/ExteriorMinimap'
import SunstudyControls from '@/components/SunstudyControls.vue'
import PointOfInterest from "@/components/overlays/PointOfInterest.vue";

export default {
  name: 'Exterior',
  components: {
    PointOfInterest,
    ExteriorMinimap,
    FilterMenuButtonMobile,
    Viewport,
    SunstudyControls,
  },
  mixins: [ActiveExteriorChecker],
  props: {
    exteriors: {
      type: Array,
      required: true,
    },
    currentExterior: {
      type: Number,
      required: false,
    },
    overlayStatus: {
      type: Boolean,
      required: true,
    },
    toggleOverlayStatus: {
      type: Function,
      required: false,
    },
    selectedOverlay: {
      type: Number,
      required: false,
    },
    toggleOverlayMobile: {
      type: Function,
      required: false,
    },
    inputClass: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      loading: false,
      isLoadedMainImg: false,
      needCentering: true,
      imgWidth: 0,
      imgHeight: 0,
    }
  },
  watch: {
    project(newProj) {
      if (newProj.length && this.units) {
        this.loading = false
      }
    },
    units(newUnits) {
      if (newUnits.length && this.project) {
        this.loading = false
      }
    },
    currentExterior() {
      this.handleResize()
      this.needCentering = true
      this.loadedMainImg();
    },
  },
  computed: {
    ...mapGetters('units', ['units', 'unitById']),
    ...mapGetters('project', [
      'statuses',
      'i18nTranslations',
      'showOverlayStatus',
      'statusMapById',
      'filterOutDisabledOverlays',
      'settings',
    ]),
    ...mapGetters('filters', ['filterOverlays']),
    ...mapGetters('exteriors', ['minimap', 'poi_list', 'isShowTogglePOI', 'isPoiMaskOn']),
    exterior() {
      const filter = this.currentExterior
        ? exterior => exterior.exterior_id === this.currentExterior
        : exterior => exterior.parent === 0

      return this.exteriors.find(filter)
    },
    filteringAllowed() {
      return this.exterior.overlays.some(overlay => this.unitById(overlay.unit_id))
    },
    overlays() {
      const overlays = this.filteringAllowed ? this.filterOverlays(this.exterior.overlays) : this.exterior.overlays

      return this.filterOutDisabledOverlays(overlays)
    },
    overlayStatusAllowed() {
      return this.$vuetify.breakpoint.mdAndUp && this.showOverlayStatus
    },
  },

  updated() {
    if (this.needCentering) {
      this.$refs.viewport.centerViewport()
      this.needCentering = false
    }
  },
  methods: {
    loadedMainImg() {
      if (this.poi_list.length === 0 && this.exterior.has_poi) {
        this.$store.dispatch('exteriors/getPoi', this.$route.params.order);
      }
      this.$store.dispatch('exteriors/isShownTogglePOI', this.exterior.has_poi);
      this.isLoadedMainImg = true;
    },
    goTo(overlay) {
      const overlayStatus = this.statuses.find(status => overlay.status_id === status.status_id)

      if (!(overlayStatus && overlayStatus.accessible)) {
        return false
      }

      if (!this.checkExteriorStatusForOverlays(overlay)) {
        return false
      }

      if (overlay.link_active && !!overlay.url) {
        window.open(overlay.url, overlay.target)
        return false
      }

      overlay.linked_exterior_id
        ? this.$router.push({
          name: 'order.overview.exterior',
          params: { order: this.$route.params.order, exteriorId: overlay.linked_exterior_id },
        })
        : this.$router.push({
          name: 'order.unit.view',
          params: {
            order: this.$route.params.order,
            unitId: overlay.unit_id,
            exteriorId: this.currentExterior,
          },
        })
    },
    getCdn(source) {
      return cdnUrl(source)
    },
    resizeAndCenter() {
      this.handleResize()
      this.$refs.viewport.centerViewport()
    },
    handleResize() {
      const scaleW = this.$el.parentElement.offsetWidth / this.exterior.width
      const scaleH = this.$el.parentElement.offsetHeight / this.exterior.height
      const scale = Math.max(scaleH, scaleW)

      if (!this.$refs.viewport) {
        return
      }

      const { $el: viewport } = this.$refs.viewport
      const viewportContent = this.$el.querySelector('#viewport-content')
      const [viewportStyleExtension, viewportContentStyleExtension] = scale === 1
        ? [
          {
            marginLeft: `${(this.$el.parentElement.offsetWidth - this.exterior.width) / 2}px`,
            marginRight: `${(this.$el.parentElement.offsetWidth - this.exterior.width) / 2}px`,
          },
          { transform: 'none' },
        ]
        : [
          {
            marginLeft: 0,
            marginRight: 0,
          },
          { transform: `scale(${scale})` },
        ]

      Object.assign(viewportContent.style, viewportContentStyleExtension)
      Object.assign(viewport.style, viewportStyleExtension, {
        width: `${this.exterior.width * scale}px`,
        height: `${this.exterior.height * scale}px`,
      })

      this.$nextTick(() => {
        this.imgWidth = this.exterior.width * scale
        this.imgHeight = this.exterior.height * scale
      });
    },
    toggleOverlay(overlay) {
      if (
        typeof this.toggleOverlayMobile === 'function'
        && (!this.selectedOverlay || this.selectedOverlay !== overlay.overlay_id)
      ) {
        this.toggleOverlayMobile(overlay.overlay_id)
      } else {
        const status = this.statuses.find(status => status.status_id === overlay.status_id)
        const unit = overlay.unit_id && this.unitById(overlay.unit_id)

        if (status && status.exturl && unit && unit.exturl) {
          window.open(unit.exturl, this.settings.unit_exturl_target)
        } else {
          this.goTo(overlay)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.v-progress-linear {
  margin-top: 0;
}
html {
  scrollbar-width: none;
}
::-webkit-scrollbar {
  display: none;
}
.viewport-container {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  /*bottom: 0;*/
  overflow-y: hidden;
  -ms-overflow-style: none;
  background-color: #363636;
}

.viewport-container-boundary {
  position: fixed;
  top: var(--app-bar-height);
  right: 0;
  left: 0;
  bottom: 0;
  /*bottom: 0;*/
  overflow-x: hidden;
  overflow-y: hidden;
  opacity: 0;
  z-index: -2;
}

.viewport {
  margin-bottom: -7px;
  background-color: #eeeeee;
}

@media screen and (orientation: portrait) {
  .viewport-container {
    overflow-x: scroll;
  }
}

@supports (-webkit-overflow-scrolling: touch) {
  .viewport-container {
    -webkit-overflow-scrolling: touch;
  }
}

#diagonalHatch path {
  stroke: rgba(var(--color-status-c), var(--hover-fill-opacity));
  stroke-width: 1
}
</style>
